import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUtensils, FaRegClock, FaFire, FaRedo, FaArrowRight, FaNotesMedical, FaTimes, FaPlus, FaHeart } from 'react-icons/fa';

export default function MealCard({ 
  meal, 
  mealType,
  onViewDetails,
  onRegenerate,
  canGenerateMeal = true,
  date
}) {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [titleFontSize, setTitleFontSize] = useState('text-lg');
  const titleRef = useRef(null);
  
  // Adjust font size based on title length
  useEffect(() => {
    if (meal && titleRef.current) {
      const title = meal.recipe.name;
      if (title.length > 25) {
        setTitleFontSize('text-sm');
      } else if (title.length > 20) {
        setTitleFontSize('text-base');
      } else {
        setTitleFontSize('text-lg');
      }
    }
  }, [meal]);
  
  const handleRegenerate = () => {
    setShowNotesModal(true);
  };
  
  const handleSubmitNotes = () => {
    onRegenerate(meal, notes);
    setShowNotesModal(false);
    setNotes('');
  };
  
  const handleCancel = () => {
    setShowNotesModal(false);
    setNotes('');
  };

  const handleGenerateSingleMeal = () => {
    onRegenerate({ date, mealType }, `Generate a ${mealType} meal`);
  };

  // Generate image URL based on cuisine type
  const getImageUrl = (cuisineType) => {
    if (!cuisineType) return `${process.env.REACT_APP_CDN_URL}/illustrations/cuisine.png`;
    
    // Convert cuisine type to lowercase and replace spaces with hyphens for consistent URL formatting
    const cuisine = cuisineType.toLowerCase().replace(/ /g, '-');
    
    // List of supported cuisine types
    const supportedCuisines = [
      'italian', 'mexican', 'indian', 'chinese', 'japanese', 
      'thai', 'mediterranean', 'french', 'american', 'greek', 
      'middle-eastern', 'korean', 'vietnamese', 'spanish', 'lebanese','south-indian'
    ];
    
    // Check if this cuisine is in our supported list
    if (supportedCuisines.includes(cuisine)) {
      return `${process.env.REACT_APP_CDN_URL}/illustrations/${cuisine}.png`;
    } else {
      // Return a generic cuisine image if specific illustration isn't available
      return `${process.env.REACT_APP_CDN_URL}/illustrations/cuisine.png`;
    }
  };

  if (!meal) {
    return (
      <motion.div 
        className="rounded-xl shadow-lg overflow-hidden border border-white/5 bg-gradient-to-br from-pri-dark/80 to-sec-dark/80 mx-auto w-full max-w-[250px] min-h-[280px] h-full flex flex-col"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        whileHover={{ y: -8, scale: 1.02, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)" }}
      >
        <div className="bg-pri-dark/60 px-4 py-2 border-b border-white/10">
          <h3 className="font-medium capitalize bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">{mealType}</h3>
        </div>
        <div className="flex-grow flex flex-col items-center justify-center p-4 text-center text-pri-white/40">
          <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-pri-dark/40 flex items-center justify-center mb-3">
            <FaUtensils className="w-8 h-8 md:w-10 md:h-10 text-white/20" />
          </div>
          <p className="mb-3">No meal planned</p>
          
          {canGenerateMeal && (
            <motion.button
              onClick={handleGenerateSingleMeal}
              className="mt-2 px-4 py-2 md:px-5 md:py-2.5 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark text-sm rounded-lg hover:shadow-lg transition-all flex items-center mx-auto font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPlus className="mr-2" />
              Generate {mealType}
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  }
  
  const { recipe } = meal;
  const imageSrc = getImageUrl(recipe.cuisineType);
  
  return (
    <>
      <motion.div 
        className="rounded-xl shadow-lg overflow-hidden border border-white/10 bg-gradient-to-br from-pri-dark/80 to-sec-dark/80 hover:shadow-xl transition-all relative group flex flex-col mx-auto w-full max-w-[250px] min-h-[280px] h-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        whileHover={{ 
          y: -8, 
          scale: 1.03, 
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
          rotateY: 2
        }}
      >
        {/* Image Container with fixed aspect ratio */}
        <div className="relative overflow-hidden bg-pri-dark/40" style={{ height: '160px' }}>
          {/* Background blurred image for loading state */}
          <div 
            className="absolute inset-0 bg-cover bg-center blur-sm scale-110"
            style={{ 
              backgroundImage: `url(${imageSrc})`,
              opacity: 0.4
            }}
          />
          
          {/* Main image */}
          <motion.img 
            src={imageSrc}
            alt={`${recipe.cuisineType} cuisine`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: imageLoaded ? 1 : 0 }}
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-pri-dark to-transparent opacity-90"></div>
          
          {/* Meal type badge */}
          <div className="absolute top-2 left-2 px-2 py-1 bg-pri-dark/40 rounded-full backdrop-blur-md">
            <span className="text-xs font-medium text-pri-dark capitalize">{mealType}</span>
          </div>
        </div>
        
        {/* Content - Fixed height for consistent card sizes */}
        <div className="p-3 flex-grow flex flex-col justify-between">
          {/* Meal name and description */}
          <div className="mb-2">
            <h4 
              ref={titleRef}
              className={`font-bold ${titleFontSize} bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text whitespace-nowrap overflow-hidden text-ellipsis`}
              title={recipe.name} // Show full title on hover
            >
              {recipe.name}
            </h4>
            <p className="text-xs text-white/80 line-clamp-1 mt-0.5">{recipe.description}</p>
          </div>
          
          {/* Meal Info */}
          <div className="grid grid-cols-3 gap-1 mb-2">
            <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1 px-1">
              <FaRegClock className="text-soft-pink mb-0.5 text-xs" />
              <span className="text-[10px] text-pri-white/70">{recipe.prepTime + recipe.cookTime} min</span>
            </div>
            
            <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1 px-1">
              <span className="text-soft-pink text-[10px] font-medium mb-0.5 capitalize">{recipe.difficulty}</span>
              <span className="text-[10px] text-pri-white/70">Level</span>
            </div>
            
            <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1 px-1">
              <FaFire className="text-orange-400 mb-0.5 text-xs" />
              <span className="text-[10px] text-pri-white/70">{recipe.nutrients.calories} cal</span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-1 mt-auto">
            <motion.button
              onClick={() => onViewDetails(meal)}
              className="py-1.5 flex-1 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark text-xs rounded-lg hover:opacity-90 transition-colors flex items-center justify-center font-medium"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              View Details
              <FaArrowRight className="ml-1 text-[10px]" />
            </motion.button>
            
            <motion.button
              onClick={handleRegenerate}
              className="py-1.5 px-1.5 text-pri-white bg-pri-dark/60 hover:text-soft-pink rounded-lg hover:bg-pri-dark/80 transition-colors border border-white/5 flex items-center justify-center"
              title="Regenerate meal"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaRedo className="w-3 h-3" />
            </motion.button>
          </div>
        </div>
        
        {/* Card elevation shadow effect */}
        <div className="absolute -inset-0.5 bg-gradient-to-b from-soft-pink/5 to-mint-green/5 rounded-xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity blur-sm"></div>
        
        {/* Hover effect overlay */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-t from-soft-pink/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
        />
      </motion.div>
      
      {/* Notes Modal */}
      <AnimatePresence>
        {showNotesModal && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCancel();
              }
            }}
          >
            <motion.div
              className="bg-gradient-to-br from-pri-dark to-sec-dark rounded-2xl shadow-2xl w-full max-w-md border border-white/10 overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-soft-pink/30 to-mint-green/30 px-6 py-5 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text flex items-center">
                    <FaUtensils className="text-soft-pink mr-2" />
                    Regenerate {mealType}
                  </h3>
                  <motion.button 
                    onClick={handleCancel}
                    className="text-white/80 hover:text-white bg-black/30 hover:bg-black/50 p-2 rounded-full transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </motion.button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="mb-5">
                  <p className="text-white/90 mb-3 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-soft-pink mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Add specific requests for your new {mealType} meal:
                  </p>
                  
                  <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 rounded-lg border border-white/10 mb-4">
                    <p className="text-sm text-white/90 mb-2 font-medium">Effective examples:</p>
                    <ul className="text-xs text-white/70 ml-6 space-y-1 list-disc">
                      <li>"Make it spicy with Indian flavors"</li>
                      <li>"Include more protein and make it low-carb"</li>
                      <li>"I want a quick 15-minute meal with simple ingredients"</li>
                      <li>"Include sweet potatoes and bell peppers"</li>
                    </ul>
                  </div>
                  
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={`Tell us what you'd like in your ${mealType} meal`}
                    className="w-full p-3 bg-pri-dark/40 border border-white/10 rounded-lg mb-2 text-white placeholder-white/40 focus:border-soft-pink focus:ring-1 focus:ring-soft-pink focus:outline-none"
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-3 justify-end mt-6">
                  <motion.button
                    onClick={handleCancel}
                    className="px-4 py-2 border border-white/10 rounded-lg text-white/70 hover:text-white hover:bg-pri-dark/40 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={handleSubmitNotes}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg hover:shadow-xl"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Regenerate
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
} 